import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useKanban } from './KanbanContext';

interface NavegacaoContextType {
  ultimaRota: string;
  rotaAtual: string;
  atualizarDados: () => Promise<void>;
}

const NavegacaoContext = createContext<NavegacaoContextType | undefined>(undefined);

interface NavegacaoProviderProps {
  children: ReactNode;
}

export const NavegacaoProvider: React.FC<NavegacaoProviderProps> = ({ children }) => {
  const location = useLocation();
  const { recarregarTarefas } = useKanban();
  const [ultimaRota, setUltimaRota] = useState<string>('');
  const [rotaAtual, setRotaAtual] = useState<string>(location.pathname);

  // Monitorar mudanças na rota
  useEffect(() => {
    if (rotaAtual !== location.pathname) {
      setUltimaRota(rotaAtual);
      setRotaAtual(location.pathname);
    }
  }, [location.pathname]);

  // Função para atualizar dados com base na rota atual
  const atualizarDados = async () => {
    try {
      // Atualizar dados com base na rota atual
      if (rotaAtual === '/') {
        // Estamos no Dashboard, recarregar dados relevantes
        console.log('Atualizando dados do Dashboard');
        // Recarregar tarefas para garantir que os dados estejam atualizados
        await recarregarTarefas();
      } else if (rotaAtual.includes('/kanban')) {
        // Estamos no Kanban, mas não vamos recarregar tarefas aqui
        // porque o componente KanbanBoard já faz isso quando é montado
        console.log('Mudando para Kanban - o componente KanbanBoard cuidará do carregamento');
        // Não chamar recarregarTarefas aqui para evitar chamadas duplicadas
      }
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
    }
  };

  // Atualizar dados quando a rota mudar
  useEffect(() => {
    // Verificar se é uma mudança real de rota e não o carregamento inicial
    if (ultimaRota !== '' && ultimaRota !== rotaAtual) {
      console.log(`Mudança de rota detectada: ${ultimaRota} -> ${rotaAtual}`);
      atualizarDados();
    }
  }, [rotaAtual, ultimaRota]);

  return (
    <NavegacaoContext.Provider
      value={{
        ultimaRota,
        rotaAtual,
        atualizarDados
      }}
    >
      {children}
    </NavegacaoContext.Provider>
  );
};

export const useNavegacao = (): NavegacaoContextType => {
  const context = useContext(NavegacaoContext);
  if (context === undefined) {
    throw new Error('useNavegacao deve ser usado dentro de um NavegacaoProvider');
  }
  return context;
};
